<script setup lang="ts">
// types
import type { Link } from '@unhead/vue';

const currentPage = defineModel<number>('modelValue', {
  default: 1,
});

const props = withDefaults(
  defineProps<{
    totalItems: number;
    itemsPerPage: number;
    maxPagesShown?: number;
  }>(),
  {
    maxPagesShown: 4,
  },
);

const { href } = useRequestURL();

const generateUrlWithPageQuery = (page: number): string => {
  const url = new URL(href);

  if (page === 1) {
    url.searchParams.delete('page');
  } else {
    url.searchParams.set('page', page.toString());
  }

  return url.toString();
};

const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage),
);
const canonicalHref = computed(() => {
  if (currentPage.value < 2 || currentPage.value > totalPages.value) return;
  return generateUrlWithPageQuery(currentPage.value);
});

const link = computed<Link[]>(() => {
  const links: Link[] = [];

  if (canonicalHref.value) {
    links.push({
      rel: 'canonical',
      href: canonicalHref.value,
    });
  }

  return links;
});

useHead({ link });
</script>

<template>
  <vue-awesome-paginate
    v-model="currentPage"
    :link-url="generateUrlWithPageQuery"
    :total-items="totalItems"
    :items-per-page="itemsPerPage"
    :max-pages-shown="maxPagesShown"
    hide-prev-next-when-ends
    type="link"
    data-component-name="AppPagination"
  >
    <template #prev-button>
      <SvgIcon name="chevron-left" />
      {{ $t('Back') }}
    </template>

    <template #next-button>
      {{ $t('Next') }}
      <SvgIcon name="chevron-right" />
    </template>
  </vue-awesome-paginate>
</template>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/mixins/typo';
@use '_/fn';

[data-component-name='AppPagination'] {
  @include flex.center-center;

  gap: 0.5rem;

  @include media.mobile {
    gap: 0.25rem;
  }

  &:deep(li) {
    .back-button,
    .next-button {
      @include flex.center-center;

      gap: 0.25rem;

      @include media.mobile {
        display: none;
      }
    }

    .back-button {
      margin-right: 0.5rem;
    }

    .next-button {
      margin-left: 0.5rem;
    }

    .paginate-buttons {
      padding: 0.5rem;

      @include typo.subtitle-3;

      color: fn.token('text-primary');

      &:not(.active-page):hover {
        background-color: rgb(fn.token('accent-hover'), 16%);
      }

      &:not(.active-page):active {
        background-color: rgb(fn.token('accent-hover'), 32%);
      }
    }

    .active-page {
      color: fn.token('text-reversed');
      background-color: fn.token('surf-cont-prim-action');
    }
  }
}
</style>
